import { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  Table,
  Container,
  Row,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Form
} from "reactstrap";
import CustomerHeader from "../../components/Headers/CustomerHeader";
import helper from '../../services/helper.service';
import customerApi from '../../services/customer.service';
import session from '../../services/session.service';

function EmployeeList() {
  const [employees, setEmployees] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [employeeData, setEmployeeData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    is_enabled: false
  });

  const clubId = session.get('clubInfo').clubId;

  useEffect(() => {
    loadEmployees();
  }, [clubId]);

  const loadEmployees = () => {
    customerApi.getEmployees({ club_id: clubId }).then((response) => {
      if (response.status === 200) {
        setEmployees(response.data);
      } else {
        helper.showToast("Error", "Failed to load employees", 3);
      }
    });
  };

  const toggleModal = () => { 
    setModalOpen(!modalOpen);
    if (!modalOpen) {
      resetForm();
    }
  };

  const resetForm = () => {
    setSelectedEmployeeId(null);
    setEmployeeData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      is_enabled: false
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEmployeeData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const isValidPhoneNumber = (phone) => {
    const phoneRegex = /^\+1\d{10}$/;
    return phoneRegex.test(phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValidPhoneNumber(employeeData.phone)) {
      helper.showToast("Error", "Phone number must be in format +1xxxyyyzzzz (10 digits after +1)", 3);
      return;
    }
    
    const payload = {
      ...employeeData,
      club_id: clubId
    };

    try {
      let response;
      if (selectedEmployeeId) {
        // Edit
        response = await customerApi.updateEmployee({ employee_id: selectedEmployeeId, ...payload });
      } else {
        // Create
        response = await customerApi.addEmployee(payload);
      }

      if (response.status === 200) {
        helper.showToast("Success", `Employee ${selectedEmployeeId ? "updated" : "created"} successfully`, 1);
        toggleModal();
        loadEmployees();
      } else {
        helper.showToast("Error", `Failed to ${selectedEmployeeId ? "update" : "create"} employee`, 3);
      }
    } catch (error) {
      helper.showToast("Error", `An error occurred while ${selectedEmployeeId ? "updating" : "creating"} employee`, 3);
    }
  };

  const onEdit = (employee) => {
    setEmployeeData({
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.email,
      phone: employee.phone,
      is_enabled: employee.is_enabled
    });
    setSelectedEmployeeId(employee.id);
    setModalOpen(true);
  };

  const onDelete = async (employeeId) => {
    try {
      const response = await customerApi.deleteEmployee({ employee_id: employeeId });
      if (response.status === 200) {
        helper.showToast("Success", "Employee deleted successfully", 1);
        setEmployees(employees.filter((employee) => employee.id !== employeeId));
      } else {
        helper.showToast("Error", "Failed to delete employee", 3);
      }
    } catch (error) {
      helper.showToast("Error", "Failed to delete employee", 3);
    }
  };

  return (
    <>
      <CustomerHeader />
      <Container className="mt--7 mb-8" fluid>
        <Card className="bg-default shadow">
          <CardHeader className="bg-transparent border-0">
            <Row className="align-items-center">
              <div className="col">
                <h2 className="text-white mb-0 text-center">Employees</h2>
              </div>
              <div className="col text-right">
                <Button color="primary" onClick={toggleModal}>
                  Add New Employee
                </Button>
              </div>
            </Row>
          </CardHeader>
          <Table className="align-items-center table-dark table-flush" responsive>
            <thead className="thead-dark">
              <tr>
                <th>No</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Enabled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((employee, index) => (
                <tr key={employee.id}>
                  <td>{index + 1}</td>
                  <td>{employee.first_name}</td>
                  <td>{employee.last_name}</td>
                  <td>{employee.email}</td>
                  <td>{employee.phone}</td>
                  <td>
                  <input
                    type="checkbox"
                    checked={employee.is_enabled}
                    onChange={async (e) => {
                      const updated = {
                        ...employee,
                        is_enabled: e.target.checked,
                        club_id: employee.club_id, // include this if your API expects it
                      };
                  
                      try {
                        const response = await customerApi.updateEmployee({
                          employee_id: employee.id,
                          ...updated
                        });
                  
                        if (response.status === 200) {
                          helper.showToast("Success", "Employee status updated", 1);
                          loadEmployees();
                        } else {
                          helper.showToast("Error", "Failed to update status", 3);
                        }
                      } catch (error) {
                        helper.showToast("Error", "Update error occurred", 3);
                      }
                    }}
                  />
                  </td>
                  <td>
                    <Button color="info" size="sm" onClick={() => onEdit(employee)}>
                      Edit
                    </Button>{' '}
                    <Button color="danger" size="sm" onClick={() => onDelete(employee.id)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      </Container>

      {/* Modal for Create/Edit */}
      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <Form onSubmit={handleSubmit}>
          <ModalHeader toggle={toggleModal}>
            {selectedEmployeeId ? "Edit Employee" : "Add New Employee"}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>First Name</Label>
              <Input
                name="first_name"
                value={employeeData.first_name}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Last Name</Label>
              <Input
                name="last_name"
                value={employeeData.last_name}
                onChange={handleChange}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                name="email"
                value={employeeData.email}
                onChange={handleChange}
              />
            </FormGroup>
            <FormGroup>
              <Label>Phone</Label>
              <Input
                name="phone"
                value={employeeData.phone}
                onChange={handleChange}
                placeholder="+1xxxyyyzzzz"
                pattern="^\+1\d{10}$"
                title="Phone number must be in format +1xxxyyyzzzz"
                required
              />
            </FormGroup>
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  name="is_enabled"
                  checked={employeeData.is_enabled}
                  onChange={handleChange}
                />{" "}
                Enabled
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" type="submit">
              {selectedEmployeeId ? "Update" : "Create"}
            </Button>
            <Button color="secondary" onClick={toggleModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
}

export default EmployeeList;
